import ks from "../ReUsableTS/kBrowserShared";
import { kEdTblSelectionMode } from "./kEditableTableTypes";
import type { kEditableCellSelection } from "./kEditableCellSelection";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableColumn } from "./kEditableColumn";

// Member bodies of kEditableCellSelection moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableCellSelection member keeps its signature and delegates here.
export class kEditableCellSelectionExpand {
    public static ClearPrev(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_PrevSelectedCells"] = [];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ClearPrev");
        }
    }

    public static SetSingleCell(Self: kEditableCellSelection, CurrCell:kEditableTableCell): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self.ParentTable){
                if (Self.ParentTable.CurrenEditableCell) { //If there was a previously selected cell
                    Self.ParentTable.CurrenEditableCell.SetCellReadMode();
                }
                if (Self.ParentTable.RowContextMenu) {
                    Self.ParentTable.RowContextMenu.OutsideClick();
                }

                CurrCell.SetSelectionMode(kEdTblSelectionMode.SingleCell);
                Self.ParentTable.CurrenEditableCell = CurrCell;
                // Fresh selection = "ready" mode: arrows navigate cells until an
                // explicit edit gesture (F2/double-click/2nd-click/drag/typing)
                // flips this back on. See kEditableTable._InlineEditActive.
                Self.ParentTable._InlineEditActive = false;
                Self.ColindexStart = CurrCell.ColumnIndex;
                Self.ColIndexEnd = CurrCell.ColumnIndex;
                Self.RowIndexStart = CurrCell.RowIndex;
                Self.RowIndexEnd = CurrCell.RowIndex;

                Self.SelectedRows = 1;
                Self.SelectedCols = 1;
                Self.SelectionMode = kEdTblSelectionMode.SingleCell;
                Self.UpdateSelectionStyles();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.SetSingleCell");
            return;
        }
    }

    public static SetSingleRowSelected(Self: kEditableCellSelection, CurrentRow:kEditableTableRow): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                Self.SelectionMode = kEdTblSelectionMode.SingleRow;
                Self.RowIndexStart = CurrentRow.Index;
                Self.RowIndexEnd = CurrentRow.Index;
                Self.ColindexStart = 0;
                Self.ColIndexEnd = Self.ParentTable.Columns.Items.length - 1;
                Self.SelectedCols = Self.ColIndexEnd - Self.ColindexStart+1;
                Self.UpdateSelectionStyles();
                Self.ClearTextSelection();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.SetSingleRowSelected");
            return ;
        }
    }

    public static SetSingleColSelected(Self: kEditableCellSelection, CurrCol:kEditableColumn): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                Self.SelectionMode = kEdTblSelectionMode.SingleColumn;
                Self.RowIndexStart = 0;
                Self.RowIndexEnd = Self.ParentTable.Rows.Items.length -1;
                // INCLUSIVE (+1), matching SetSingleRowSelected. Fixed 2026-07-27: the exclusive
                // form stored 1 for a TWO-row table, which _CheckSingleCell then read as 1 x 1 and
                // collapsed the whole-column selection to a single cell. Same root cause as the
                // 2x2 range collapse above; pinned by C15b.
                Self.SelectedRows = Self.RowIndexEnd - Self.RowIndexStart + 1;
                Self.ColindexStart = CurrCol.Index;
                Self.ColIndexEnd = CurrCol.Index;
                Self.SelectedCols = 1;
                Self.UpdateSelectionStyles();
                Self.ClearTextSelection();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.SetSingleColSelected");
            return ;
        }
    }

    public static ClearTextSelection(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Sel: Selection | null = window.getSelection();
            if (Sel === null) { return; }
            // Selection.empty() is a WebKit/Blink alias that Firefox does not
            // implement, so calling it blind used to THROW on every Firefox
            // paint — which the old catch swallowed with an "//Ignore errors
            // here" comment (a constitution law-1 violation: the error had no
            // fate at all). Feature-detect it instead; removeAllRanges() is the
            // standard call and is sufficient on its own. Anything that still
            // throws is a real defect and now reaches the global handler.
            const EmptyFn: unknown = (Sel as unknown as { empty?: unknown }).empty;
            if (typeof EmptyFn === "function") { Sel.empty(); }
            Sel.removeAllRanges();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ClearTextSelection");
            return ;
        }
    }

    public static SelectAll(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                Self.SelectionMode = kEdTblSelectionMode.SelectAll;
                Self.RowIndexStart =  0;
                Self.RowIndexEnd = Self.ParentTable.Rows.Items.length -1;
                Self.ColindexStart = 0;
                Self.ColIndexEnd = Self.ParentTable.Columns.Items.length - 1;
                Self.SelectedCols = Self.ParentTable.Columns.Items.length;
                Self.SelectedRows = Self.ParentTable.Rows.Items.length;
            }
            Self.UpdateSelectionStyles();
            Self.ClearTextSelection();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.SelectAll");
            return ;
        }
    }

    public static _CheckSingleCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                if(Self.SelectedCols === 1){
                    if(Self.SelectedRows ===1){
                        Self.SelectionMode = kEdTblSelectionMode.SingleCell;
                        Self.ParentTable.CurrenEditableCell = Self.ParentTable.GetCellByIndexes(Self.RowIndexStart,Self.ColindexStart);
                    }
                }
                if(Self.SelectionMode!== kEdTblSelectionMode.SingleCell){
                    if(Self.ParentTable.CurrenEditableCell){
                        Self.ParentTable.CurrenEditableCell.SetCellReadMode();
                        Self.ParentTable.CurrenEditableCell = null;
                    }
                }
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._CheckSingleCell");
            return ;
        }
    }

    public static ExpandRight(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                switch(Self.SelectionMode){
                    case kEdTblSelectionMode.SingleCell: Self["_ExpandRightFromCell"](); break;
                    case kEdTblSelectionMode.CustomExtRightDown:
                    case kEdTblSelectionMode.CustomExtRightUp: Self["_ExpandRightWiden"](); break;
                    case kEdTblSelectionMode.CustomExtLeftDown:
                    case kEdTblSelectionMode.CustomExtLeftUp: Self["_ExpandRightFromLeft"](); break;
                    case kEdTblSelectionMode.SingleColumn: Self["_ExpandRightFromColumn"](); break;
                    case kEdTblSelectionMode.MultipleColsExtendRight: Self["_ExpandRightMoreCols"](); break;
                }
                Self["_CheckSingleCell"]();
                Self.UpdateSelectionStyles();
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ExpandRight");
            return ;
        }
    }

    /** Moved from kEditableCellSelection._ExpandRightFromCell (size split); the class member delegates here. */
    public static _ExpandRightFromCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            Self.SelectionMode = kEdTblSelectionMode.CustomExtRightDown;
            Self.SelectedCols ++;
            Self.ColIndexEnd ++;
            if((Self.ColIndexEnd)>Self.ParentTable.Columns.Items.length){
                Self.SelectedCols --;
                Self.ColIndexEnd --;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightFromCell"); }
    }

    /** Moved from kEditableCellSelection._ExpandRightWiden (size split); the class member delegates here. */
    public static _ExpandRightWiden(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            Self.ColIndexEnd ++;
            if(Self.ColIndexEnd>(Self.ParentTable.Columns.Items.length-1)){
                Self.ColIndexEnd --;
            }else{
                Self.SelectedCols ++;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightWiden"); }
    }

    /** Moved from kEditableCellSelection._ExpandRightFromLeft (size split); the class member delegates here. */
    public static _ExpandRightFromLeft(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.SelectedCols===1){
                if(Self.SelectionMode === kEdTblSelectionMode.CustomExtLeftDown){
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtRightDown;
                }else{
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtRightUp;
                }
                Self.SelectedCols++;
                Self.ColIndexEnd ++;
            }else{
                Self.SelectedCols --;
                Self.ColindexStart ++;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightFromLeft"); }
    }

    /** Moved from kEditableCellSelection._ExpandRightFromColumn (size split); the class member delegates here. */
    public static _ExpandRightFromColumn(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let Max: number = -1;
            if (Self.ParentTable !== null) { Max = Self.ParentTable.Columns.Items.length; }
            Self["_ExpandRightCols"](Max, kEdTblSelectionMode.MultipleColsExtendRight, "kEditableCellSelection._ExpandRightFromColumn");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightFromColumn");
        }
    }

    /** Moved from kEditableCellSelection._ExpandRightMoreCols (size split); the class member delegates here. */
    public static _ExpandRightMoreCols(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let Max: number = -1;
            if (Self.ParentTable !== null) { Max = Self.ParentTable.Columns.Items.length - 1; }
            Self["_ExpandRightCols"](Max, null, "kEditableCellSelection._ExpandRightMoreCols");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightMoreCols");
        }
    }

    /** Moved from kEditableCellSelection._ExpandRightCols (size split); the class member delegates here. */
    public static _ExpandRightCols(Self: kEditableCellSelection, Max: number, SetMode: kEdTblSelectionMode | null, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Max < 0){ return; }
            Self.SelectedCols++;
            Self.ColIndexEnd ++;
            if(Self.ColIndexEnd > Max){
                Self.SelectedCols --;
                Self.ColIndexEnd --;
            }else if(SetMode !== null){
                Self.SelectionMode = SetMode;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandRightCols"); }
    }

    public static ExpandUp(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                switch(Self.SelectionMode){
                    case kEdTblSelectionMode.SingleCell: Self["_ExpandUpFromCell"](); break;
                    case kEdTblSelectionMode.CustomExtRightDown:
                    case kEdTblSelectionMode.CustomExtLeftDown: Self["_ExpandUpFromDown"](); break;
                    case kEdTblSelectionMode.CustomExtRightUp:
                    case kEdTblSelectionMode.CustomExtLeftUp:
                    case kEdTblSelectionMode.MultipleRowsExtendUp: Self["_ExpandUpHeighten"](); break;
                    case kEdTblSelectionMode.SingleRow: Self["_ExpandUpFromRow"](); break;
                    case kEdTblSelectionMode.MultipleRowsExtendDown: Self["_ExpandUpShrinkDown"](); break;
                    }
                Self.UpdateSelectionStyles();
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ExpandUp");
            return ;
        }
    }

    /** Moved from kEditableCellSelection._ExpandUpFromCell (size split); the class member delegates here. */
    public static _ExpandUpFromCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.SelectionMode = kEdTblSelectionMode.CustomExtRightUp;
            Self.RowIndexStart --;
            if(Self.RowIndexStart<0){
                Self.RowIndexStart ++;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandUpFromCell"); }
    }

    /** Moved from kEditableCellSelection._ExpandUpFromDown (size split); the class member delegates here. */
    public static _ExpandUpFromDown(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.RowIndexEnd>0){
                if(Self.SelectedRows >1){
                    Self.RowIndexEnd --;
                }else{
                    if(Self.SelectionMode ===kEdTblSelectionMode.CustomExtLeftDown){
                        Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftUp;
                    }else{
                        Self.SelectionMode = kEdTblSelectionMode.CustomExtRightUp;
                    }
                    Self.RowIndexStart --;
                }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandUpFromDown"); }
    }

    /** Moved from kEditableCellSelection._ExpandUpHeighten (size split); the class member delegates here. */
    public static _ExpandUpHeighten(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.RowIndexStart>0){
                Self.RowIndexStart --;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandUpHeighten"); }
    }

    /** Moved from kEditableCellSelection._ExpandUpFromRow (size split); the class member delegates here. */
    public static _ExpandUpFromRow(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.RowIndexStart>0){
                Self.RowIndexStart --;
                Self.SelectedRows = 2;
                Self.SelectionMode = kEdTblSelectionMode.MultipleRowsExtendUp;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandUpFromRow"); }
    }

    /** Moved from kEditableCellSelection._ExpandUpShrinkDown (size split); the class member delegates here. */
    public static _ExpandUpShrinkDown(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.RowIndexEnd >0){
                Self.RowIndexEnd --;
                if(Self.SelectedRows === 1){
                    Self.SelectionMode = kEdTblSelectionMode.SingleRow;
                }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandUpShrinkDown"); }
    }

    public static ExpandDown(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                switch(Self.SelectionMode){
                    case kEdTblSelectionMode.SingleCell: Self["_ExpandDownFromCell"](); break;
                    case kEdTblSelectionMode.CustomExtRightDown:
                    case kEdTblSelectionMode.CustomExtLeftDown:
                    case kEdTblSelectionMode.MultipleRowsExtendDown: Self["_ExpandDownHeighten"](); break;
                    case kEdTblSelectionMode.CustomExtRightUp:
                    case kEdTblSelectionMode.CustomExtLeftUp: Self["_ExpandDownFromUp"](); break;
                    case kEdTblSelectionMode.SingleRow: Self["_ExpandDownFromRow"](); break;
                    case kEdTblSelectionMode.MultipleRowsExtendUp: Self["_ExpandDownShrinkUp"](); break;
                    }
                Self.UpdateSelectionStyles();
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ExpandDown");
            return ;
        }
    }

    /** Moved from kEditableCellSelection._ExpandDownFromCell (size split); the class member delegates here. */
    public static _ExpandDownFromCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ExpandDownSeed"](kEdTblSelectionMode.CustomExtRightDown, "kEditableCellSelection._ExpandDownFromCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownFromCell");
        }
    }

    /** Moved from kEditableCellSelection._ExpandDownHeighten (size split); the class member delegates here. */
    public static _ExpandDownHeighten(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ExpandDownSeed"](null, "kEditableCellSelection._ExpandDownHeighten");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownHeighten");
        }
    }

    /** Moved from kEditableCellSelection._ExpandDownFromUp (size split); the class member delegates here. */
    public static _ExpandDownFromUp(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.SelectedRows>1){
                Self.RowIndexStart ++;
            }else{
                if(Self.SelectionMode === kEdTblSelectionMode.CustomExtRightUp){
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtRightDown;
                }else{
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftDown;
                }
                Self.RowIndexEnd ++;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownFromUp"); }
    }

    /** Moved from kEditableCellSelection._ExpandDownFromRow (size split); the class member delegates here. */
    public static _ExpandDownFromRow(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ExpandDownSeed"](kEdTblSelectionMode.MultipleRowsExtendDown, "kEditableCellSelection._ExpandDownFromRow");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownFromRow");
        }
    }

    /** The two ExpandDown seed shapes in one shell: below the last row, grow it, mark 2 rows, set the mode. */
    /** Moved from kEditableCellSelection._ExpandDownSeed (size split); the class member delegates here. */
    public static _ExpandDownSeed(Self: kEditableCellSelection, Mode: kEdTblSelectionMode | null, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            if(Self.RowIndexEnd<(Self.ParentTable.Rows.Items.length -1)){
                Self.RowIndexEnd ++;
                if (Mode !== null) { Self.SelectedRows = 2; Self.SelectionMode = Mode; }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownSeed"); }
    }

    /** Moved from kEditableCellSelection._ExpandDownShrinkUp (size split); the class member delegates here. */
    public static _ExpandDownShrinkUp(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.RowIndexStart ++;
            if(Self.SelectedRows === 1){
                Self.SelectionMode = kEdTblSelectionMode.SingleRow;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandDownShrinkUp"); }
    }

    public static ExpandLeft(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                switch(Self.SelectionMode){
                    case kEdTblSelectionMode.SingleCell: Self["_ExpandLeftFromCell"](); break;
                    case kEdTblSelectionMode.CustomExtLeftDown:
                    case kEdTblSelectionMode.CustomExtLeftUp: Self["_ExpandLeftWiden"](); break;
                    case kEdTblSelectionMode.CustomExtRightDown:
                    case kEdTblSelectionMode.CustomExtRightUp: Self["_ExpandLeftFromRight"](); break;
                    case kEdTblSelectionMode.SingleColumn: Self["_ExpandLeftFromColumn"](); break;
                    case kEdTblSelectionMode.MultipleColsExtendLeft: Self["_ExpandLeftMoreCols"](); break;
                    case kEdTblSelectionMode.MultipleColsExtendRight: Self["_ExpandLeftFewerCols"](); break;
                }
                Self.UpdateSelectionStyles();
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand.ExpandLeft");
            return ;
        }
    }

    /** Moved from kEditableCellSelection._ExpandLeftFromCell (size split); the class member delegates here. */
    public static _ExpandLeftFromCell(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftDown;
            Self.SelectedCols ++;
            Self.ColindexStart --;
            if(Self.ColindexStart<0){
                Self.SelectedCols --;
                Self.ColindexStart ++;
                Self.SelectionMode = kEdTblSelectionMode.SingleCell;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftFromCell"); }
    }

    /** Moved from kEditableCellSelection._ExpandLeftWiden (size split); the class member delegates here. */
    public static _ExpandLeftWiden(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.ColindexStart --;
            if(Self.ColindexStart<0){
                Self.ColindexStart =0;
            }else{
                Self.SelectedCols ++;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftWiden"); }
    }

    /** Moved from kEditableCellSelection._ExpandLeftFromRight (size split); the class member delegates here. */
    public static _ExpandLeftFromRight(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.SelectedCols>1){
                Self.SelectedCols --;
                Self.ColIndexEnd --;
            }else{
                if(Self.SelectionMode === kEdTblSelectionMode.CustomExtRightDown){
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftDown;
                }else{
                    Self.SelectionMode = kEdTblSelectionMode.CustomExtLeftUp;
                }
                Self.SelectedCols++;
                Self.ColindexStart --;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftFromRight"); }
    }

    /** Moved from kEditableCellSelection._ExpandLeftFromColumn (size split); the class member delegates here. */
    public static _ExpandLeftFromColumn(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ExpandLeftCols"](kEdTblSelectionMode.MultipleColsExtendLeft, "kEditableCellSelection._ExpandLeftFromColumn");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftFromColumn");
        }
    }

    /** Moved from kEditableCellSelection._ExpandLeftMoreCols (size split); the class member delegates here. */
    public static _ExpandLeftMoreCols(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_ExpandLeftCols"](null, "kEditableCellSelection._ExpandLeftMoreCols");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftMoreCols");
        }
    }

    /** Moved from kEditableCellSelection._ExpandLeftCols (size split); the class member delegates here. */
    public static _ExpandLeftCols(Self: kEditableCellSelection, SetMode: kEdTblSelectionMode | null, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.SelectedCols++;
            Self.ColindexStart --;
            if(Self.ColindexStart < 0){
                Self.SelectedCols --;
                Self.ColindexStart = 0;
            }else if(SetMode !== null){
                Self.SelectionMode = SetMode;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftCols"); }
    }

    /** Moved from kEditableCellSelection._ExpandLeftFewerCols (size split); the class member delegates here. */
    public static _ExpandLeftFewerCols(Self: kEditableCellSelection): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.SelectedCols--;
            Self.ColIndexEnd --;
            if(Self.ColIndexEnd <0){
                Self.SelectedCols --;
                Self.ColindexStart = 0;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kEditableCellSelectionExpand._ExpandLeftFewerCols"); }
    }
}
